/**
 * HardshipApplicationForm.tsx
 *
 * Drop this anywhere on the homeschool or elite landing pages:
 *   import HardshipApplicationForm from '@/components/HardshipApplicationForm'
 *   <HardshipApplicationForm applicationType="homeschool" />
 *
 * Props:
 *   applicationType: 'homeschool' | 'elite' | 'bundle'  (default: 'homeschool')
 *   triggerLabel:    custom button label (optional)
 */

import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Heart, X, ChevronRight, CheckCircle2, Loader2, Users, MessageSquare, DollarSign, MapPin, Phone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/integrations/supabase/client'

type ApplicationType = 'homeschool' | 'elite' | 'bundle' | 'university'
type HardshipType = 'full' | 'partial' | 'temporary'

interface Props {
    applicationType?: ApplicationType
    triggerLabel?: string
}

export default function HardshipApplicationForm({ applicationType = 'homeschool', triggerLabel }: Props) {
    const { t } = useTranslation()

    const PLAN_LABELS: Record<ApplicationType, string> = useMemo(() => ({
        homeschool: t('hardship.plans.homeschool'),
        elite: t('hardship.plans.elite'),
        bundle: t('hardship.plans.bundle'),
        university: t('hardship.plans.university'),
    }), [t])
    const [open, setOpen] = useState(false)
    const [step, setStep] = useState(1) // 1 = personal, 2 = children, 3 = story
    const [loading, setLoading] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const [form, setForm] = useState({
        applicant_name: '',
        applicant_email: '',
        phone: '',
        country: '',
        number_of_children: '',
        children_ages: '',
        hardship_type: '' as HardshipType | '',
        monthly_budget: '',
        story: '',
        referral_source: '',
    })

    const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }))

    const canProceed1 = form.applicant_name.trim() && form.applicant_email.includes('@') && form.hardship_type
    const canProceed2 = form.number_of_children !== ''
    const canSubmit = form.story.trim().length >= 50

    async function handleSubmit() {
        setLoading(true)
        setError(null)
        try {
            const { data, error } = await supabase.functions.invoke('hardship-application', {
                body: {
                    ...form,
                    number_of_children: form.number_of_children ? parseInt(form.number_of_children) : null,
                    application_type: applicationType,
                },
            })

            if (error) throw error
            setSubmitted(true)
        } catch (e: any) {
            console.error('Submission error:', e)
            setError(e.message || t('hardship.common.error'))
        } finally {
            setLoading(false)
        }
    }

    const close = () => {
        setOpen(false)
        setTimeout(() => { setStep(1); setSubmitted(false); setError(null) }, 400)
    }

    return (
        <>
            {/* Trigger */}
            <button
                onClick={() => setOpen(true)}
                className="group inline-flex items-center gap-2 text-sm font-bold text-emerald-700 hover:text-emerald-800 transition-colors underline underline-offset-4 decoration-emerald-300 hover:decoration-emerald-600"
            >
                <Heart size={14} className="text-emerald-500 group-hover:scale-110 transition-transform" />
                {triggerLabel || t('hardship.trigger')}
            </button>

            {/* Modal overlay */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
                        onClick={e => e.target === e.currentTarget && close()}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94, y: 16 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.94, y: 16 }}
                            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                            className="relative bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden max-h-[92vh] overflow-y-auto"
                        >
                            {/* Header */}
                            <div className="sticky top-0 z-10 bg-white px-8 pt-8 pb-4 border-b border-slate-100">
                                <button
                                    onClick={close}
                                    className="absolute top-5 right-5 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                                >
                                    <X size={16} className="text-slate-500" />
                                </button>
                                <div className="flex items-center gap-3 mb-1">
                                    <div className="w-10 h-10 rounded-2xl bg-emerald-100 flex items-center justify-center">
                                        <Heart size={18} className="text-emerald-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-black text-slate-900 leading-tight">{t('hardship.title')}</h2>
                                        <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest text-[#10b981]">
                                            {PLAN_LABELS[applicationType]}
                                        </p>
                                    </div>
                                </div>
                                {/* Step indicator */}
                                {!submitted && (
                                    <div className="flex items-center gap-2 mt-4">
                                        {[1, 2, 3].map(s => (
                                            <div
                                                key={s}
                                                className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${s <= step ? 'bg-emerald-500' : 'bg-slate-200'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="px-8 py-6">
                                {submitted ? (
                                    /* Success state */
                                    <motion.div
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-center py-8"
                                    >
                                        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                                            <CheckCircle2 size={40} className="text-emerald-600" />
                                        </div>
                                        <h3 className="text-2xl font-black text-slate-900 mb-3">{t('hardship.success.title')}</h3>
                                        <p className="text-slate-500 leading-relaxed mb-2">
                                            {t('hardship.success.desc1')}
                                        </p>
                                        <p className="text-slate-400 text-sm mb-8">
                                            {t('hardship.success.desc2', { email: form.applicant_email })}
                                        </p>
                                        <button
                                            onClick={close}
                                            className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors"
                                        >
                                            {t('hardship.success.done')}
                                        </button>
                                    </motion.div>
                                ) : (
                                    <>
                                        {/* Step 1: Personal details + assistance type */}
                                        {step === 1 && (
                                            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
                                                <div>
                                                    <p className="text-sm font-bold text-slate-700 mb-4">
                                                        {t('hardship.step1.confidentiality')}
                                                    </p>
                                                </div>

                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">{t('hardship.step1.nameLabel')}</label>
                                                        <input
                                                            type="text"
                                                            value={form.applicant_name}
                                                            onChange={e => set('applicant_name', e.target.value)}
                                                            placeholder={t('hardship.step1.namePlaceholder')}
                                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all text-[#111827]"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">{t('hardship.step1.emailLabel')}</label>
                                                        <input
                                                            type="email"
                                                            value={form.applicant_email}
                                                            onChange={e => set('applicant_email', e.target.value)}
                                                            placeholder={t('hardship.step1.emailPlaceholder')}
                                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all text-[#111827]"
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">
                                                                <Phone size={10} className="inline mr-1" />{t('hardship.step1.phoneLabel')}
                                                            </label>
                                                            <input
                                                                type="tel"
                                                                value={form.phone}
                                                                onChange={e => set('phone', e.target.value)}
                                                                placeholder={t('hardship.step1.phonePlaceholder')}
                                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all text-[#111827]"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">
                                                                <MapPin size={10} className="inline mr-1" />{t('hardship.step1.countryLabel')}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={form.country}
                                                                onChange={e => set('country', e.target.value)}
                                                                placeholder={t('hardship.step1.countryPlaceholder')}
                                                                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all text-[#111827]"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">{t('hardship.step1.assistanceTypeLabel')}</label>
                                                        <div className="grid grid-cols-1 gap-2">
                                                            {([
                                                                { value: 'full', label: t('hardship.step1.types.full.label'), desc: t('hardship.step1.types.full.desc') },
                                                                { value: 'partial', label: t('hardship.step1.types.partial.label'), desc: t('hardship.step1.types.partial.desc') },
                                                                { value: 'temporary', label: t('hardship.step1.types.temporary.label'), desc: t('hardship.step1.types.temporary.desc') },
                                                            ] as { value: HardshipType; label: string; desc: string }[]).map(opt => (
                                                                <button
                                                                    key={opt.value}
                                                                    type="button"
                                                                    onClick={() => set('hardship_type', opt.value)}
                                                                    className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 text-left transition-all ${form.hardship_type === opt.value
                                                                        ? 'border-emerald-500 bg-emerald-50'
                                                                        : 'border-slate-200 bg-slate-50 hover:border-emerald-300'
                                                                        }`}
                                                                >
                                                                    <div>
                                                                        <p className={`text-sm font-bold ${form.hardship_type === opt.value ? 'text-emerald-700' : 'text-slate-700'}`}>{opt.label}</p>
                                                                        <p className="text-xs text-slate-400 font-medium">{opt.desc}</p>
                                                                    </div>
                                                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.hardship_type === opt.value ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                                                                        {form.hardship_type === opt.value && <div className="w-2 h-2 rounded-full bg-white" />}
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => setStep(2)}
                                                    disabled={!canProceed1}
                                                    className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                                                >
                                                    {t('hardship.common.continue')} <ChevronRight size={16} />
                                                </button>
                                            </motion.div>
                                        )}

                                        {/* Step 2: Children + budget */}
                                        {step === 2 && (
                                            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
                                                <div>
                                                    <h3 className="text-base font-black text-slate-900 flex items-center gap-2 mb-1 text-[#0f172a]">
                                                        <Users size={16} className="text-emerald-500" /> {t('hardship.step2.title')}
                                                    </h3>
                                                    <p className="text-sm text-slate-400 font-medium">{t('hardship.step2.subtitle')}</p>
                                                </div>

                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">{t('hardship.step2.childrenCountLabel')}</label>
                                                        <select
                                                            value={form.number_of_children}
                                                            onChange={e => set('number_of_children', e.target.value)}
                                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all text-[#111827]"
                                                        >
                                                            <option value="">{t('hardship.step2.childrenCountSelect')}</option>
                                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                                                                <option key={n} value={n}>{t('hardship.step2.childrenCountOption', { count: n })}</option>
                                                            ))}
                                                            <option value="9">{t('hardship.step2.nineOrMore')}</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">{t('hardship.step2.childrenAgesLabel')}</label>
                                                        <input
                                                            type="text"
                                                            value={form.children_ages}
                                                            onChange={e => set('children_ages', e.target.value)}
                                                            placeholder={t('hardship.step2.childrenAgesPlaceholder')}
                                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all text-[#111827]"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">
                                                            <DollarSign size={10} className="inline mr-1" />{t('hardship.step2.budgetLabel')}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={form.monthly_budget}
                                                            onChange={e => set('monthly_budget', e.target.value)}
                                                            placeholder={t('hardship.step2.budgetPlaceholder')}
                                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all text-[#111827]"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">{t('hardship.step2.referralLabel')}</label>
                                                        <input
                                                            type="text"
                                                            value={form.referral_source}
                                                            onChange={e => set('referral_source', e.target.value)}
                                                            placeholder={t('hardship.step2.referralPlaceholder')}
                                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all text-[#111827]"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex gap-3">
                                                    <button
                                                        onClick={() => setStep(1)}
                                                        className="flex-1 py-3.5 border-2 border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 transition-all"
                                                    >
                                                        {t('hardship.common.back')}
                                                    </button>
                                                    <button
                                                        onClick={() => setStep(3)}
                                                        disabled={!canProceed2}
                                                        className="flex-1 py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                                                    >
                                                        {t('hardship.common.continue')} <ChevronRight size={16} />
                                                    </button>
                                                </div>
                                            </motion.div>
                                        )}

                                        {/* Step 3: Story */}
                                        {step === 3 && (
                                            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
                                                <div>
                                                    <h3 className="text-base font-black text-slate-900 flex items-center gap-2 mb-1 text-[#0f172a]">
                                                        <MessageSquare size={16} className="text-emerald-500" /> {t('hardship.step3.title')}
                                                    </h3>
                                                    <p className="text-sm text-slate-400 font-medium leading-relaxed">
                                                        {t('hardship.step3.subtitle')}
                                                    </p>
                                                </div>

                                                <div>
                                                    <textarea
                                                        value={form.story}
                                                        onChange={e => set('story', e.target.value)}
                                                        placeholder={t('hardship.step3.storyPlaceholder')}
                                                        rows={7}
                                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-[#111827] font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all resize-none leading-relaxed"
                                                    />
                                                    <p className={`text-xs font-medium mt-1.5 text-right ${form.story.length < 50 ? 'text-slate-400' : 'text-emerald-500'}`}>
                                                        {form.story.length < 50
                                                            ? `${50 - form.story.length} ${t('hardship.step3.charNeeded')}`
                                                            : t('hardship.step3.readyToSubmit')}
                                                    </p>
                                                </div>

                                                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                                                    <p className="text-xs font-bold text-emerald-700 mb-1">{t('hardship.step3.privacyTitle')}</p>
                                                    <p className="text-xs text-emerald-600 leading-relaxed">
                                                        {t('hardship.step3.privacyDesc')}
                                                    </p>
                                                </div>

                                                {error && (
                                                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                                        <p className="text-sm text-red-600 font-medium">{error}</p>
                                                    </div>
                                                )}

                                                <div className="flex gap-3">
                                                    <button
                                                        onClick={() => setStep(2)}
                                                        disabled={loading}
                                                        className="flex-1 py-3.5 border-2 border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 transition-all disabled:opacity-50"
                                                    >
                                                        {t('hardship.common.back')}
                                                    </button>
                                                    <button
                                                        onClick={handleSubmit}
                                                        disabled={!canSubmit || loading}
                                                        className="flex-1 py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                                                    >
                                                        {loading ? (
                                                            <><Loader2 size={16} className="animate-spin" /> {t('hardship.common.submitting')}</>
                                                        ) : (
                                                            <><Heart size={16} /> {t('hardship.common.submit')}</>
                                                        )}
                                                    </button>
                                                </div>
                                            </motion.div>
                                        )}
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}